/**
 * Node types for the worklist `filters` predicate tree, plus the response-status
 * branch each view needs.
 *
 * The backend compiles this tree into the query's WHERE clause.
 */

import { CmmResponseStatus } from "../../../enums/cmmResponseStatus";
import { CmmStpStatus } from "../../../enums/cmmStpStatus";

/** Leaf comparison in the predicate tree. */
export type WorklistFilterCondition = {
  type: "condition";
  field: string;
  operator: string;
  value?: string[];
};

/** AND / OR group in the predicate tree. */
export type WorklistFilterGroup = {
  type: "group";
  operator: "and" | "or";
  children: (WorklistFilterCondition | WorklistFilterGroup)[];
};

/** Column holding the payer's response to the request. */
export const RESPONSE_STATUS_COLUMN = "response_status";

/**
 * Builds a leaf matching any of the given values.
 *
 * @input field - Column name; dot notation allowed for nested JSON fields
 * @input value - Values to match
 * @return Condition node
 */
export const isAnyOf = (
  field: string,
  value: string[],
): WorklistFilterCondition => ({
  type: "condition",
  field,
  operator: "is_any_of",
  value,
});

/**
 * Alternate spellings written to `response_status` over time.
 *
 * RPA / STP use title-case "File"; the Call Summary form historically wrote
 * lowercase "file". Predicate matching is case-sensitive, so filters must
 * include both when either is selected.
 */
const RESPONSE_STATUS_ALIASES: Record<string, string[]> = {
  [CmmResponseStatus.APPROVAL_ON_FILE]: [
    CmmResponseStatus.APPROVAL_ON_FILE,
    CmmStpStatus.APPROVAL_ON_FILE,
  ],
  [CmmStpStatus.APPROVAL_ON_FILE]: [
    CmmResponseStatus.APPROVAL_ON_FILE,
    CmmStpStatus.APPROVAL_ON_FILE,
  ],
  [CmmResponseStatus.DENIAL_ON_FILE]: [
    CmmResponseStatus.DENIAL_ON_FILE,
    CmmStpStatus.DENIAL_ON_FILE,
  ],
  [CmmStpStatus.DENIAL_ON_FILE]: [
    CmmResponseStatus.DENIAL_ON_FILE,
    CmmStpStatus.DENIAL_ON_FILE,
  ],
};

/**
 * Expands selected response statuses to include known DB spelling variants.
 *
 * @input statuses - Values picked in the Response Status filter panel
 * @return Deduped list safe to send in an `is_any_of` condition
 */
export const expandResponseStatusValues = (statuses: string[]): string[] => {
  const expanded = new Set<string>();
  for (const status of statuses) {
    const aliases = RESPONSE_STATUS_ALIASES[status] ?? [status];
    for (const alias of aliases) {
      expanded.add(alias);
    }
  }
  return [...expanded];
};

/**
 * Response statuses offered in the non-ePA worklist's Response Status filter.
 *
 * Includes CMM/STP statuses the RPA writes to `response_status`, plus the six
 * values the Call Summary modal persists via
 * {@link CALLING_RESPONSE_STATUS_OPTIONS}, so a saved summary can be found by
 * filtering on its own status. On-file statuses use the STP title-case spelling
 * that RPA writes; {@link expandResponseStatusValues} still matches older
 * lowercase rows. The non-ePA fetch itself does not filter on response status —
 * this only narrows the list once the user picks from the filter panel.
 */
export const NON_EPA_RESPONSE_STATUSES: string[] = [
  CmmResponseStatus.NA_OUTCOME,
  CmmStpStatus.NON_EPA,
  CmmStpStatus.PA_RESOLVED,
  CmmResponseStatus.NOT_CORRECT_PROCESSOR,
  CmmStpStatus.OTHER_PLATFORMS,
  CmmResponseStatus.ONSHORE_ASSISTANCE,
  CmmResponseStatus.APPROVED,
  CmmResponseStatus.AUTH_NOT_REQUIRED,
  CmmResponseStatus.DENIED,
  CmmStpStatus.APPROVAL_ON_FILE,
  CmmStpStatus.DENIAL_ON_FILE,
  CmmResponseStatus.PENDING,
  CmmResponseStatus.SEND_TO_CLIENT,
];

/**
 * Matches every response status except Pending, including orders with no status
 * yet — `is_not_any_of` alone would drop those, since `NOT (NULL IN (...))` never
 * holds in BigQuery.
 *
 * @return OR group covering "not Pending" and "no status recorded"
 */
export const buildNotPendingFilter = (): WorklistFilterGroup => ({
  type: "group",
  operator: "or",
  children: [
    {
      type: "condition",
      field: RESPONSE_STATUS_COLUMN,
      operator: "is_not_any_of",
      value: [CmmResponseStatus.PENDING],
    },
    {
      type: "condition",
      field: RESPONSE_STATUS_COLUMN,
      operator: "is_empty",
    },
  ],
});
